import { DestroyRef, inject, Injectable } from '@angular/core';
import { NavigationEnd, NavigationError, NavigationStart, Router } from '@angular/router';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { BUILD_ID } from '../../../environments/build-id';

/** Minimum gap between two `version.json` checks. */
const CHECK_INTERVAL_MS = 10 * 60 * 1000;
/** A second reload from the same build within this window is skipped (loop guard). */
const RELOAD_GUARD_MS = 60 * 1000;
const RELOAD_GUARD_KEY = 'app-update-last-reload';

const CHUNK_ERROR_PATTERNS = [
  /ChunkLoadError/i,
  /Failed to fetch dynamically imported module/i,
  /error loading dynamically imported module/i,
  /Importing a module script failed/i,
  /is not a valid JavaScript MIME type/i,
];

/** True for errors raised when a lazy chunk of the running build no longer exists. */
export function isChunkLoadError(err: unknown): boolean {
  const text = err instanceof Error ? `${err.name}: ${err.message}` : String(err);
  return CHUNK_ERROR_PATTERNS.some((pattern) => pattern.test(text));
}

/**
 * Full page load to `url` so the browser picks up the current deploy.
 * Returns false (and does nothing) if this build already reloaded within {@link RELOAD_GUARD_MS}.
 */
export function reloadTo(url: string, buildId: string = BUILD_ID): boolean {
  const now = Date.now();
  try {
    const last = parseGuard(sessionStorage.getItem(RELOAD_GUARD_KEY));
    if (last?.buildId === buildId && now - last.ts < RELOAD_GUARD_MS) {
      return false;
    }
    sessionStorage.setItem(RELOAD_GUARD_KEY, JSON.stringify({ buildId, ts: now }));
  } catch {
    // Without the guard a chunk failing on startup would reload forever: fail closed.
    return false;
  }
  AppUpdateService.hardNavigate(url);
  return true;
}

/** Malformed entries count as "no previous reload". */
function parseGuard(raw: string | null): { buildId: string; ts: number } | null {
  if (raw === null) {
    return null;
  }
  try {
    const value = JSON.parse(raw) as { buildId?: unknown; ts?: unknown } | null;
    return typeof value?.buildId === 'string' && typeof value.ts === 'number'
      ? { buildId: value.buildId, ts: value.ts }
      : null;
  } catch {
    return null;
  }
}

/** Passes `promise` through; a missing chunk of a stale build reloads the current page. */
export function reloadOnChunkError<T>(promise: Promise<T>): Promise<T> {
  return promise.catch((err: unknown) => {
    if (isChunkLoadError(err)) {
      reloadTo(window.location.pathname + window.location.search + window.location.hash);
    }
    throw err;
  });
}

/**
 * Moves open tabs onto a new deploy: notices a changed `version.json` and turns the next
 * router navigation into a full page load; reloads when a lazy chunk of the old build is gone.
 */
@Injectable({ providedIn: 'root' })
export class AppUpdateService {
  /** Overridable in tests. */
  static hardNavigate = (url: string): void => window.location.assign(url);

  protected readonly buildId: string = BUILD_ID;
  private updatePending = false;
  private lastCheck = 0;

  constructor() {
    const router = inject(Router);
    const destroyRef = inject(DestroyRef);

    router.events.pipe(takeUntilDestroyed(destroyRef)).subscribe((event) => {
      if (event instanceof NavigationStart && this.updatePending) {
        reloadTo(event.url, this.buildId);
      } else if (event instanceof NavigationError && isChunkLoadError(event.error)) {
        reloadTo(event.url, this.buildId);
      } else if (event instanceof NavigationEnd) {
        void this.checkForUpdate();
      }
    });

    const onVisible = () => {
      if (document.visibilityState === 'visible') {
        void this.checkForUpdate();
      }
    };
    document.addEventListener('visibilitychange', onVisible);
    destroyRef.onDestroy(() => document.removeEventListener('visibilitychange', onVisible));
  }

  /** Fetches the deployed build id (throttled) and flags a pending update on mismatch. */
  async checkForUpdate(): Promise<void> {
    if (this.buildId === 'local' || this.updatePending) {
      return;
    }
    const now = Date.now();
    if (now - this.lastCheck < CHECK_INTERVAL_MS) {
      return;
    }
    this.lastCheck = now;

    const deployed = await this.fetchDeployedBuildId();
    if (deployed && deployed !== this.buildId) {
      this.updatePending = true;
    }
  }

  protected async fetchDeployedBuildId(): Promise<string | null> {
    try {
      const response = await fetch('/version.json', { cache: 'no-cache' });
      if (!response.ok) {
        return null;
      }
      const body = (await response.json()) as { buildId?: unknown };
      return typeof body.buildId === 'string' ? body.buildId : null;
    } catch {
      return null;
    }
  }
}
